import { defineStore } from 'pinia'
import {
  getArchives,
  getFolders,
  searchFileStore,
  getFilesForFolder
} from './FileStoreApi'
import { logger } from '@/logger'
import { EventBus, Events } from '@/eventbus/EventBus'

function nestFolders(folders, id = null) {
  return folders
    .filter((folder) => folder.parentId === id)
    .map((folder) => ({
      ...folder,
      subFolders: nestFolders(folders, folder.folderId)
    }))
}

export const useFileStoreStore = defineStore('fileStore', {
  state: () => ({
    archives: [],
    folders: [],
    files: [],
    trash: [],
    searchResults: [],
    searchOptions: {
      includeTrash: true,
      searchOption: 1,
      folderId: undefined,
      searchString: ''
    },
    orderSelectedFiles: {
      orderId: '',
      filenames: []
    },
    treeBrowserSelectedFolderId: null,
    browserSelectedFileIds: [],
    selectedTrashArchive: undefined,
    hydrating: false,
    searchInProgress: false,
    refreshing: false
  }),

  getters: {
    hasArchives: (state) => state.archives.length > 0,
    searchResultCount: (state) => state.searchResults.length,
    getArchives: (state) => () => state.archives,
    getEnabledArchives: (state) => () =>
      state.archives.filter((a) => !a.isDisabled),
    getArchive: (state) => (id) =>
      state.archives.find((a) => a.archiveId === id),
    getArchiveByName: (state) => (name) =>
      state.archives.find((a) => a.name === name),
    getArchiveByFolderId: (state) => (id) =>
      state.archives.find((a) => a.folderId === id),
    getArchiveUsers: (state) => (id) =>
      state.archives.find((a) => a.archiveId === id)?.users || [],
    getFolder: (state) => (id) => state.folders.find((f) => f.folderId === id),
    getFile: (state) => (id) => state.files.find((f) => f.fileId === id),
    getSubFolders: (state) => (parentId) =>
      state.folders.filter((f) => f.parentId === parentId),
    getFilesForFolder: (state) => (id) =>
      state.files.filter((f) => f.folderId === id),
    getFolderPath: (state) => (id) => {
      let path = []
      let folder = state.folders.find((f) => f.folderId === id)
      while (folder) {
        path.unshift({ name: folder.name, id: folder.folderId })
        folder = state.folders.find((f) => f.folderId === folder.parentId)
      }
      return path
    },
    getFolderTree: (state) => nestFolders(state.folders),
    getFolderTreeForArchive: (state) => (archiveId) =>
      nestFolders(state.folders.filter((f) => f.archiveId === archiveId)),
    hasSelectedFiles: (state) =>
      state.browserSelectedFileIds.length > 0 ||
      state.orderSelectedFiles.filenames.length > 0,
    selectedFiles: (state) =>
      state.files.filter((f) =>
        state.browserSelectedFileIds.includes(f.fileId)
      ),
    hasMultipleSelectedFiles: (state) =>
      state.browserSelectedFileIds.length > 1,
    isHydrating: (state) => state.hydrating
  },

  actions: {
    async hydrate() {
      if (this.hydrating) return
      this.hydrating = true
      try {
        const [archives, folders] = await Promise.all([
          getArchives(),
          getFolders()
        ])
        this.archives = archives
        this.folders = folders
      } finally {
        this.hydrating = false
        this.refreshing = false
      }
    },
    async loadFilesForFolder(folderId) {
      this.files = []
      try {
        const files = await getFilesForFolder(folderId)
        this.files = files
      } catch {
        EventBus.emit(Events.FOLDER_NOT_FOUND)
      }
    },
    async search(searchString) {
      this.searchInProgress = true
      this.searchOptions.searchString = searchString
      this.searchResults = []
      try {
        const results = await searchFileStore({
          ...this.searchOptions,
          searchString
        })
        this.searchResults = results
      } catch (err) {
        logger.error(err)
        this.searchResults = []
      } finally {
        this.searchInProgress = false
      }
    },
    async refreshArchives() {
      this.archives = await getArchives()
    },
    async refreshFolders() {
      this.folders = await getFolders()
    },
    clear() {
      this.archives = []
      this.folders = []
      this.files = []
      this.trash = []
      this.searchResults = []
      this.searchOptions = {}
      this.treeBrowserSelectedFolderId = null
      this.browserSelectedFileIds = []
    },
    addFolder(folder) {
      this.folders = [...this.folders, folder]
    },
    trashFolder(folderId) {
      this.folders = this.folders.filter((f) => f.folderId !== folderId)
    },
    trashFile(trashedFileId) {
      this.files = this.files.filter((f) => f.fileId !== trashedFileId)
    },
    renameFolder({ folderId, newName }) {
      const folder = this.folders.find((f) => f.folderId === folderId)
      if (folder) folder.name = newName
    },
    renameArchive({ folderId, newName }) {
      const archive = this.archives.find((a) => a.folderId === folderId)
      if (archive) archive.name = newName
    },
    moveFolder({ folderId, parentId }) {
      const folder = this.folders.find((f) => f.folderId === folderId)
      if (folder) folder.parentId = parentId
    },
    renameFile({ fileId, newName }) {
      const file = this.files.find((f) => f.fileId === fileId)
      if (file) file.name = newName
      const searchFile = this.searchResults.find((f) => f.fileId === fileId)
      if (searchFile) searchFile.name = newName
    },
    restoreFile(file) {
      this.trash = this.trash.filter((f) => f.fileId !== file.fileId)
    },
    moveFiles({ fileIds, folderId }) {
      fileIds.forEach((fileId) => {
        const file = this.files.find((f) => f.fileId === fileId)
        if (file) file.folderId = folderId
      })
    },
    setSearchOptions(searchOptions) {
      this.searchOptions = { ...this.searchOptions, ...searchOptions }
    },
    clearBrowserSelectedFileIds() {
      this.browserSelectedFileIds = []
    },
    browserDeSelectFileId(fileId) {
      this.browserSelectedFileIds = this.browserSelectedFileIds.filter(
        (id) => id !== fileId
      )
    },
    browserSelectedFileId(fileId) {
      if (!this.browserSelectedFileIds.includes(fileId)) {
        this.browserSelectedFileIds.push(fileId)
      }
    }
  }
})
